import React, { useState } from 'react';

import {
  ActionMeta,
  SingleValue,
} from '@next/react-select';
import Async, { AsyncProps } from '@next/react-select/async';
import debounce from 'debounce-promise';
import {
  FormatOptionLabelMeta,
  OptionTypeBase,
} from 'react-select';

import { theme } from '@waglabs/base-theme';
import { Box } from '@waglabs/box-next';

import {
  getAddress,
  getPlaceFromMapbox,
  MapboxGeocodingFeature,
  ParsedAddress,
} from '../../../utils/mapbox/geocoding';
import {
  FieldHelperText,
  FieldHelperTextProps,
} from '../../FieldHelperText';

export type AutocompleteProps = Pick<AsyncProps<{
  label: string;
  value: MapboxGeocodingFeature;
}, false, never>, 'id' | 'name' | 'styles' | 'options' | 'value' | 'placeholder'> & {
  onChange?: (params: {
    label: string,
    value: MapboxGeocodingFeature,
    address: ParsedAddress | undefined,
  } | null, actionMeta: ActionMeta<{
    label: string;
    value: MapboxGeocodingFeature;
  }>) => void;
  helperText?: string;
  helperTextProps?: FieldHelperTextProps;
  streetAddressComplete?: boolean;
  error?: boolean;
};

export const Autocomplete = ({
  onChange,
  helperText,
  helperTextProps,
  streetAddressComplete = false,
  error,
  styles,
  options,
  ...rest
}: AutocompleteProps) => {
  const [items, setItems] = useState<{
    label: string;
    value: MapboxGeocodingFeature;
  }[]>(options as unknown as []);

  const loadOptions = async (searchQuery: string) => {
    const body = await getPlaceFromMapbox(searchQuery);

    const data = body.features.map((feature) => {
      const label = feature.place_name;

      return {
        label,
        value: feature,
      };
    });

    setItems(data);
    return data;
  };

  const handleOnLoadOptions = debounce(loadOptions, 300);

  const handleOnChange = (
    selectedOption: SingleValue<{
      label: string;
      value: MapboxGeocodingFeature;
    }>,
    actionMeta: ActionMeta<{
      label: string;
      value: MapboxGeocodingFeature;
    }>,
  ) => {
    const label = selectedOption?.label;
    const value = selectedOption?.value;

    if (actionMeta.action === 'clear') {
      onChange?.(null, actionMeta);
      return;
    }

    if (!label) {
      return;
    }

    if (!value?.context) {
      return;
    }

    const address = getAddress(value);
    onChange?.({
      label,
      value,
      address,
    }, actionMeta);
  };

  const handleFormatOptionLabel = (
    selectedOption: SingleValue<{
      label: string;
      value: MapboxGeocodingFeature;
    }>,
    context: FormatOptionLabelMeta<OptionTypeBase, false>,
  ) => {
    if (!selectedOption) {
      return undefined;
    }

    if (context.context === 'value') {
      const address = getAddress(selectedOption.value);
      /*
    The streetAddressComplete prop determines whether to show the
    full street address including the street name, city, state and
    zipcode, in cases where that is shown as other text fields
    around the autocomplete address field, the street address
    alone will suffice so that is rendered in the Autocomplete
    compoenent as it's value
 */
      if (!streetAddressComplete) {
        return address.streetAddress;
      }
      return address.addressString;
    }

    return selectedOption.label;
  };

  return (
    <Box>
      <Async
        id="mapbox-autocomplete"
        cacheOptions
        isClearable
        defaultOptions={items}
        loadOptions={handleOnLoadOptions}
        onChange={handleOnChange}
        formatOptionLabel={handleFormatOptionLabel}
        classNamePrefix="react-select"
        {...rest}
        styles={{
          ...styles as unknown as Record<string, unknown>,
          control: (provided, state) => ({
            ...provided,
            padding: '8px',
            borderRadius: '8px',
            borderWidth: '1px',
            borderStyle: 'solid',
            maxHeight: '52px',
            borderColor: (state.selectProps as unknown as AutocompleteProps).error
              ? theme.colors.red
              // TODO - Add this to our theme colors
              : '#D1D1D1',
            ...styles?.control,
          }),
          singleValue: (provided) => ({
            ...provided,
            color: theme.colors.dimGray,
            fontFamily: theme.fonts.muli,
            ...styles?.singleValue,
          }),
          placeholder: (provided) => ({
            ...provided,
            // TODO - Add this to our theme colors
            color: '#aaaaaa',
            fontFamily: theme.fonts.muli,
          }),
        }}
      />
      {helperText && (
        <FieldHelperText
          text={helperText}
          error={error}
          {...helperTextProps}
        />
      )}
    </Box>
  );
};
